// @ts-nocheck
import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, Star } from 'lucide-react';
import { EmailHeaderTemplates } from '@/components/admin/EmailHeaderTemplates';

interface SocialLinks {
  facebook?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
}

interface HeaderFooter {
  id: string;
  name: string;
  type: 'header' | 'footer';
  content: string;
  is_default: boolean;
  is_active: boolean;
  social_links?: SocialLinks;
  created_at: string;
  updated_at: string;
}

const EmailHeadersFooters: React.FC = () => {
  const [headers, setHeaders] = useState<HeaderFooter[]>([]);
  const [footers, setFooters] = useState<HeaderFooter[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<'header' | 'footer' | null>(null);

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    type: 'header' | 'footer';
    content: string;
    is_default: boolean;
    is_active: boolean;
    social_links: Required<SocialLinks>;
  }>({
    name: '',
    type: 'header' as 'header' | 'footer',
    content: '',
    is_default: false,
    is_active: true,
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      tiktok: '',
    },
  });

  useEffect(() => {
    fetchHeadersFooters();
  }, []);

  const fetchHeadersFooters = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_headers_footers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const items = data as HeaderFooter[];
      setHeaders(items.filter(item => item.type === 'header'));
      setFooters(items.filter(item => item.type === 'footer'));
    } catch (error) {
      console.error('Error fetching headers/footers:', error);
      toast.error('Failed to load headers and footers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (id?: string) => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast.error('Name and content are required');
      return;
    }

    try {
      if (id) {
        // Update existing
        const { error } = await supabase
          .from('email_headers_footers')
          .update({
            name: formData.name,
            social_links: formData.social_links,
            content: formData.content,
            is_default: formData.is_default,
            is_active: formData.is_active,
          })
          .eq('id', id);

        if (error) throw error;
        toast.success('Updated successfully');
      } else {
        // Create new
        const { error } = await supabase
          .from('email_headers_footers')
          .insert({
            name: formData.name,
            type: formData.type,
            content: formData.content,
            is_default: formData.is_default,
            is_active: formData.is_active,
          });

        if (error) throw error;
        toast.success('Created successfully');
      }

      setEditingId(null);
      setIsCreating(null);
      resetForm();
      fetchHeadersFooters();
    } catch (error) {
      console.error('Error saving header/footer:', error);
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const { error } = await supabase
        .from('email_headers_footers')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Deleted successfully');
      fetchHeadersFooters();
    } catch (error) {
      console.error('Error deleting header/footer:', error);
      toast.error('Failed to delete');
    }
  };

  const handleSetDefault = async (id: string, type: 'header' | 'footer') => {
    try {
      const { error } = await supabase
        .from('email_headers_footers')
        .update({ is_default: true })
        .eq('id', id);

      if (error) throw error;
      toast.success(`Set as default ${type}`);
      fetchHeadersFooters();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error('Failed to set default');
    }
  };

  const handleEdit = (item: HeaderFooter) => {
    setEditingId(item.id);
    setIsCreating(null);
    const defaultSocialLinks = {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      tiktok: '',
    };
    setFormData({
      name: item.name,
      type: item.type,
      content: item.content,
      is_default: item.is_default,
      is_active: item.is_active,
      social_links: {
        ...defaultSocialLinks,
        ...item.social_links,
      },
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setIsCreating(null);
    resetForm();
  };

  const handleCreate = (type: 'header' | 'footer') => {
    setIsCreating(type);
    setEditingId(null);
    setFormData({
      name: '',
      type,
      content: '',
      is_default: false,
      is_active: true,
      social_links: {
        facebook: '',
        twitter: '',
        instagram: '',
        linkedin: '',
        youtube: '',
        tiktok: '',
      },
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setIsCreating(null);
    setFormData({
      name: '',
      type: 'header',
      content: '',
      is_default: false,
      is_active: true,
      social_links: {
        facebook: '',
        twitter: '',
        instagram: '',
        linkedin: '',
        youtube: '',
        tiktok: '',
      },
    });
  };

  const renderItems = (items: HeaderFooter[], type: 'header' | 'footer') => (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {type === 'header' ? 'Email Headers' : 'Email Footers'}
        </h3>
        <Button onClick={() => handleCreate(type)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add {type === 'header' ? 'Header' : 'Footer'}
        </Button>
      </div>

      {/* Create new form */}
      {isCreating === type && (
        <Card className="border-2 border-primary">
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Company Header"
              />
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <div className="border rounded-md">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content: value })}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'align': [] }],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link', 'image'],
                      ['clean']
                    ]
                  }}
                  style={{ minHeight: '200px' }}
                />
              </div>
            </div>

            {/* Social Media Links - Show only for footers */}
            {type === 'footer' && (
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-sm font-semibold">Social Media Links</Label>
                <p className="text-xs text-muted-foreground mb-2">Add your social media URLs (optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="facebook" className="text-xs">Facebook</Label>
                    <Input
                      id="facebook"
                      value={formData.social_links.facebook}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, facebook: e.target.value }
                      })}
                      placeholder="https://facebook.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="twitter" className="text-xs">Twitter/X</Label>
                    <Input
                      id="twitter"
                      value={formData.social_links.twitter}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, twitter: e.target.value }
                      })}
                      placeholder="https://twitter.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instagram" className="text-xs">Instagram</Label>
                    <Input
                      id="instagram"
                      value={formData.social_links.instagram}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, instagram: e.target.value }
                      })}
                      placeholder="https://instagram.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedin" className="text-xs">LinkedIn</Label>
                    <Input
                      id="linkedin"
                      value={formData.social_links.linkedin}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, linkedin: e.target.value }
                      })}
                      placeholder="https://linkedin.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youtube" className="text-xs">YouTube</Label>
                    <Input
                      id="youtube"
                      value={formData.social_links.youtube}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, youtube: e.target.value }
                      })}
                      placeholder="https://youtube.com/..."
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tiktok" className="text-xs">TikTok</Label>
                    <Input
                      id="tiktok"
                      value={formData.social_links.tiktok}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        social_links: { ...formData.social_links, tiktok: e.target.value }
                      })}
                      placeholder="https://tiktok.com/@..."
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
                <Label>Active</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_default: checked })
                  }
                />
                <Label>Set as Default</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={() => handleSubmit()}>
                Create
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {items.length === 0 && !isCreating ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No {type}s created yet
          </CardContent>
        </Card>
      ) : (
        items.map((item) => (
          <Card key={item.id} className={!item.is_active ? 'opacity-60' : ''}>
            {editingId === item.id ? (
              // Edit mode
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Content</Label>
                  <div className="border rounded-md">
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'color': [] }, { 'background': [] }],
                          [{ 'align': [] }],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['link', 'image'],
                          ['clean']
                        ]
                      }}
                      style={{ minHeight: '200px' }}
                    />
                  </div>
                </div>

                {/* Social Media Links - Show only for footers */}
                {type === 'footer' && (
                  <div className="space-y-3 pt-2 border-t">
                    <Label className="text-sm font-semibold">Social Media Links</Label>
                    <p className="text-xs text-muted-foreground mb-2">Add your social media URLs (optional)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="edit-facebook" className="text-xs">Facebook</Label>
                        <Input
                          id="edit-facebook"
                          value={formData.social_links.facebook}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            social_links: { ...formData.social_links, facebook: e.target.value }
                          })}
                          placeholder="https://facebook.com/..."
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-twitter" className="text-xs">Twitter/X</Label>
                        <Input
                          id="edit-twitter"
                          value={formData.social_links.twitter}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            social_links: { ...formData.social_links, twitter: e.target.value }
                          })}
                          placeholder="https://twitter.com/..."
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-instagram" className="text-xs">Instagram</Label>
                        <Input
                          id="edit-instagram"
                          value={formData.social_links.instagram}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            social_links: { ...formData.social_links, instagram: e.target.value }
                          })}
                          placeholder="https://instagram.com/..."
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-linkedin" className="text-xs">LinkedIn</Label>
                        <Input
                          id="edit-linkedin"
                          value={formData.social_links.linkedin}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            social_links: { ...formData.social_links, linkedin: e.target.value }
                          })}
                          placeholder="https://linkedin.com/..."
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-youtube" className="text-xs">YouTube</Label>
                        <Input
                          id="edit-youtube"
                          value={formData.social_links.youtube}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            social_links: { ...formData.social_links, youtube: e.target.value }
                          })}
                          placeholder="https://youtube.com/..."
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-tiktok" className="text-xs">TikTok</Label>
                        <Input
                          id="edit-tiktok"
                          value={formData.social_links.tiktok}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            social_links: { ...formData.social_links, tiktok: e.target.value }
                          })}
                          placeholder="https://tiktok.com/@..."
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_active: checked })
                      }
                    />
                    <Label>Active</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.is_default}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_default: checked })
                      }
                    />
                    <Label>Set as Default</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleSubmit(item.id)}>
                    Update
                  </Button>
                </div>
              </CardContent>
            ) : (
              // View mode
              <>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base">{item.name}</CardTitle>
                      {item.is_default && (
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {!item.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(item.id, type)}
                        >
                          Set as Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(item)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <CardDescription>
                    {item.is_active ? 'Active' : 'Inactive'}
                    {item.is_default && ' • Default'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded bg-muted p-3 text-sm max-h-40 overflow-auto">
                    <div dangerouslySetInnerHTML={{ __html: item.content }} />
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Email Headers & Footers</h1>
        <p className="text-muted-foreground mt-2">
          Manage reusable headers and footers for system emails
        </p>
      </div>

      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="headers">Headers ({headers.length})</TabsTrigger>
          <TabsTrigger value="footers">Footers ({footers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <EmailHeaderTemplates />
        </TabsContent>

        <TabsContent value="headers" className="space-y-4">
          {renderItems(headers, 'header')}
        </TabsContent>

        <TabsContent value="footers" className="space-y-4">
          {renderItems(footers, 'footer')}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailHeadersFooters;
